import { useQueryClient, useQueryErrorResetBoundary } from '@tanstack/react-query';
import { useRouter, type ErrorComponentProps } from '@tanstack/react-router';
import { RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { toDisplayableError } from '@/components/states';
import { isApiError } from '@/lib/api/errors';

/**
 * The error boundary every route falls back to.
 *
 * Two things matter here beyond showing a sentence:
 *
 * 1. **Reset without a reload.** `window.location.reload()` would work, and it
 *    would also throw away the display name, the theme choice, scroll position
 *    and — in Phase 2 — anything held in memory alongside the session. Instead
 *    the cache is cleared, the router's error state is reset, and the loaders
 *    re-run. Nothing touches `localStorage` or the session cookie, so the user
 *    comes back to the same page as the same person.
 *
 * 2. **The cache really is cleared.** A route that failed because of poisoned
 *    cached data would fail again on a plain re-render, so "Try again" has to
 *    mean something stronger than "render once more".
 */
export const RouteErrorBoundary = ({ error, reset }: ErrorComponentProps) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const queryErrorReset = useQueryErrorResetBoundary();

  const message = toDisplayableError(error);

  /*
   * A stack is noise for an API failure the server already explained, but it is
   * the whole story for a render error in an internal tool. Show it only when
   * the error did not come from the API.
   */
  const detail =
    !isApiError(error) && error instanceof Error ? error.stack : undefined;

  const handleReset = () => {
    queryClient.clear();
    queryErrorReset.reset();
    reset();
    void router.invalidate();
  };

  return (
    <div
      role="alert"
      className="mx-auto flex max-w-xl flex-col items-start gap-4 py-20"
    >
      <div className="space-y-2">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Something went wrong
        </h1>
        <p className="text-sm text-muted-foreground">{message}</p>
      </div>

      {detail === undefined ? null : (
        <details className="w-full">
          <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
            Technical detail
          </summary>
          <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-muted p-3 text-xs leading-relaxed text-muted-foreground">
            {detail}
          </pre>
        </details>
      )}

      <Button onClick={handleReset} variant="outline" size="sm">
        <RotateCcw className="size-4" />
        Try again
      </Button>
    </div>
  );
};

/** Shown when no route matches the URL. */
export const RouteNotFound = () => {
  const router = useRouter();

  return (
    <div className="mx-auto flex max-w-xl flex-col items-start gap-4 py-20">
      <div className="space-y-2">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Page not found
        </h1>
        <p className="text-sm text-muted-foreground">
          That address does not match anything in Atlas. It may have been
          deleted, or the link may be wrong.
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          void router.navigate({ to: '/' });
        }}
      >
        Back to projects
      </Button>
    </div>
  );
};
